import { useCallback, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { ChatApiFactory } from "../lusca-search-api/LuscaSearchApiFactory";
import { ChatMessage, Stage } from "../lusca-search-api/generated-src";

export interface ChatEntry {
  id: string;
  role: "user" | "assistant";
  content: string;
  isError?: boolean;
}

let entryCounter = 0;
const nextId = (): string => {
  entryCounter += 1;
  return `chat-entry-${entryCounter}`;
};

// History is client-side only: every request re-sends the full
// conversation, so the backend stays stateless.
export default function useChat(stage: Stage, transactionId: string) {
  const [entries, setEntries] = useState<ChatEntry[]>([]);
  // AI-suggested follow-ups for the latest answer. Fetched on a separate
  // request AFTER the answer renders, so it never delays the answer.
  const [aiFollowups, setAiFollowups] = useState<string[]>([]);
  // True while the follow-up call is in flight, so the UI can show a loading
  // state instead of flashing generic chips that then swap to the AI ones.
  const [followupsLoading, setFollowupsLoading] = useState(false);

  const mutation = useMutation({
    mutationFn: async (history: ChatMessage[]) => {
      const api = ChatApiFactory();
      const { data } = await api.chat(stage, {
        transactionId,
        messages: history,
      });
      return data;
    },
  });

  // Guards against stale follow-up responses: each call takes a request id, and
  // only the latest one is allowed to apply state. A new sendMessage bumps the
  // id, so an in-flight response from a previous question is ignored.
  const followupRequestId = useRef(0);

  // Best-effort fast Haiku call for follow-ups; failures leave the list empty.
  const fetchFollowups = useCallback(
    (history: ChatMessage[]) => {
      const api = ChatApiFactory();
      const requestId = ++followupRequestId.current;
      const isLatest = () => requestId === followupRequestId.current;
      setFollowupsLoading(true);
      api
        .chatFollowups(stage, { transactionId, messages: history })
        .then(({ data }) => {
          if (isLatest()) setAiFollowups(data.followups ?? []);
        })
        .catch(() => {
          if (isLatest()) setAiFollowups([]);
        })
        .finally(() => {
          if (isLatest()) setFollowupsLoading(false);
        });
    },
    [stage, transactionId]
  );

  const sendMessage = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || mutation.isLoading) return;

      const nextEntries: ChatEntry[] = [
        ...entries.filter((e) => !e.isError),
        { id: nextId(), role: "user", content: trimmed },
      ];
      setEntries(nextEntries);
      // Clear the previous answer's follow-ups while the new one is pending, and
      // invalidate any in-flight follow-up call so its stale response is ignored.
      followupRequestId.current += 1;
      setAiFollowups([]);
      setFollowupsLoading(false);

      const history = nextEntries.map(({ role, content }) => ({
        role,
        content,
      }));
      mutation.mutate(history, {
        onSuccess: (data) => {
          const answer = data.content ?? "(no response)";
          setEntries((prev) => [
            ...prev,
            { id: nextId(), role: "assistant", content: answer },
          ]);
          // After the answer renders, fetch tailored follow-ups (separate call).
          fetchFollowups([...history, { role: "assistant", content: answer }]);
        },
        onError: (error) => {
          setEntries((prev) => [
            ...prev,
            {
              id: nextId(),
              role: "assistant",
              content: `Something went wrong: ${
                error instanceof Error ? error.message : "unknown error"
              }. Please try again.`,
              isError: true,
            },
          ]);
        },
      });
    },
    [entries, mutation, fetchFollowups]
  );

  const reset = useCallback(() => {
    setEntries([]);
    setAiFollowups([]);
    setFollowupsLoading(false);
  }, []);

  return {
    entries,
    aiFollowups,
    followupsLoading,
    sendMessage,
    reset,
    isLoading: mutation.isLoading,
  };
}
